import React, { useState } from 'react';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { ApiError } from '../../lib/api';
import { sendWhatsappReminderTest, type WhatsappReminderLog } from '../../lib/events-api';
import { useApiJson } from '../../hooks/useApi';
import { cn } from '../../lib/utils';

const inputClass =
  'min-h-11 w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2 px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)]';

export function WhatsappReminderTools({ eventId }: { eventId: string }) {
  const { data, mutate } = useApiJson<{ ok: boolean; reminders: WhatsappReminderLog[] }>(
    `/api/events/${eventId}/whatsapp-reminders`,
  );
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const reminders = data?.reminders ?? [];

  const send = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setBusy(true);
    setMessage(null);
    setFailed(false);
    try {
      await sendWhatsappReminderTest(eventId, phone);
      setPhone('');
      setMessage('Test message sent.');
    } catch (err) {
      setFailed(true);
      setMessage(err instanceof ApiError ? err.message : 'Could not send the test message');
    } finally {
      setBusy(false);
      await mutate();
    }
  };

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 space-y-3">
      <div>
        <h4 className="text-sm font-medium text-[var(--color-text-primary)]">WhatsApp reminder</h4>
        <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
          Sends one template message for this event. It does not mark a registration as reminded.
        </p>
      </div>
      <form onSubmit={(ev) => void send(ev)} className="flex flex-col sm:flex-row gap-2">
        <label className="flex-1">
          <span className="sr-only">Phone number</span>
          <input
            type="tel"
            name="reminderPhone"
            inputMode="tel"
            autoComplete="tel"
            placeholder="10-digit mobile"
            className={inputClass}
            value={phone}
            onChange={(ev) => setPhone(ev.target.value)}
            required
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center justify-center min-h-11 px-3 rounded-[var(--radius-md)] text-sm font-medium bg-[var(--color-accent)] text-white disabled:opacity-50"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
          Send test to me
        </button>
      </form>
      {message && (
        <p className={cn('text-xs', failed ? 'text-[var(--color-error-text)]' : 'text-[var(--color-success-text)]')}>
          {message}
        </p>
      )}
      {reminders.length === 0 ? (
        <p className="text-xs text-[var(--color-text-tertiary)]">No reminders sent yet.</p>
      ) : (
        <ul className="space-y-1.5 max-h-40 overflow-y-auto">
          {reminders.map((row) => (
            <li key={row.id} className="text-xs text-[var(--color-text-secondary)]">
              <span className="tabular-nums">{format(new Date(row.createdAt), 'd MMM, p')}</span>
              {' · '}
              <span className="font-medium text-[var(--color-text-primary)]">{row.phone}</span>
              {' · '}
              <span className={row.status === 'sent' ? 'text-[var(--color-success-text)]' : 'text-[var(--color-error-text)]'}>
                {row.status === 'sent' ? 'Sent' : 'Failed'}
              </span>
              {row.registrationId ? null : ' · Test'}
              {row.error ? <span className="block text-[var(--color-error-text)] mt-0.5">{row.error}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
